import { extractNameParts } from '@st/util/human-name'
import { Task, defModule, type TaskMessage, type UpdateResult } from '@st/redux'
import type { FolderId, Question } from '@st/sdk'
import { Effect } from 'effect'
import { match, P } from '@st/pattern'
import { SdkClient } from '~/effects'

type Context = {
  folderId: FolderId
  contractId: string
  signerId: string
}

export type Identity = {
  firstName: string
  lastName: string
  dateOfBirth: string
  ssn: string
}

type IdentityVerificationSession = Context & {
  identity: Identity
  detailsPromptShown: boolean
}

/**
 * The provider hands back the whole question set at once, but the signer
 * answers one at a time: `questionIndex` is the question on screen and
 * `answers` keeps every selection made so far, so stepping back and forward
 * shows the earlier choice rather than clearing it. Forward navigation requires
 * an answer, so reaching the last question means every earlier one is answered.
 */
type QuestionSession = IdentityVerificationSession & {
  sessionId: string
  questions: Question[]
  questionIndex: number
  answers: Record<string, number>
}

export type IdentityVerificationState =
  | (IdentityVerificationSession & { status: 'identity' })
  | (IdentityVerificationSession & { status: 'starting' })
  | (IdentityVerificationSession & { status: 'retryQuiz' })
  | { status: 'handoff' }
  | (QuestionSession & { status: 'questions' })
  | (QuestionSession & { status: 'submitting' })
  | { status: 'completed' }

// Leaving verification discards local input and revokes Task delivery; it does
// not undo server-side processing. If the reopened contract still requires
// verification, this child starts from identity entry; startKBA then decides
// whether to issue a fresh quiz or report an identity refusal.
// Keys exclude personal identity details and answers.
// Every KBA status is a message; a transport or server failure has no product
// response yet, so it is a defect.
const startVerification = Task.define(
  (state: IdentityVerificationSession) => Effect.orDie(startKBA(state)),
  { key: (state) => JSON.stringify([state.folderId, state.contractId, state.signerId]) }
)
const submitVerification = Task.define((state: QuestionSession) => Effect.orDie(submitKBA(state)), {
  key: (state) => state.sessionId
})

export type IdentityVerificationMessage =
  | { type: 'identityChanged'; field: keyof Identity; sensitiveValue: string }
  | { type: 'startClicked' }
  | { type: 'answerSelected'; questionId: string; sensitiveAnswerId: number }
  | { type: 'previousQuestionClicked' }
  // Continue means "go on from here": the next question, submitting the last
  // one. The phase decides which.
  | { type: 'continueClicked' }
  | TaskMessage<typeof startVerification>
  | TaskMessage<typeof submitVerification>

// The enclosing verification phase owns lifetime. Context is captured once
// for the child's requests; there is no independently synchronized identity.
export function initialIdentityVerification(
  input: Context & { name: string }
): IdentityVerificationState {
  return {
    status: 'identity',
    detailsPromptShown: false,
    folderId: input.folderId,
    contractId: input.contractId,
    signerId: input.signerId,
    identity: { ...extractNameParts(input.name), dateOfBirth: '', ssn: '' }
  }
}

export const identityVerificationModule = defModule<{
  state: IdentityVerificationState
  message: IdentityVerificationMessage
  init: Context & { name: string }
}>({
  init: initialIdentityVerification,
  tasks: (state) =>
    match(state)
      .with({ status: 'starting' }, (state) => [startVerification(state)])
      .with({ status: 'submitting' }, (state) => [submitVerification(state)])
      .with(
        {
          status: P.union('identity', 'retryQuiz', 'handoff', 'questions', 'completed')
        },
        () => []
      )
      .exhaustive(),
  update: (state, message): UpdateResult<IdentityVerificationState, IdentityVerificationMessage> =>
    match({ state, message })
      .returnType<UpdateResult<IdentityVerificationState, IdentityVerificationMessage>>()
      .with(
        { state: { status: 'identity' }, message: { type: 'identityChanged' } },
        ({ state, message }) => ({
          ...state,
          identity: { ...state.identity, [message.field]: message.sensitiveValue }
        })
      )
      .with(
        { state: { status: P.union('identity', 'retryQuiz') }, message: { type: 'startClicked' } },
        ({ state }) => ({
          ...state,
          status: 'starting'
        })
      )
      .with(
        {
          state: { status: 'starting' },
          message: {
            type: 'startReturned',
            status: 'ready',
            sensitiveSession: P.nonNullable
          }
        },
        ({ state, message }) => ({
          ...state,
          status: 'questions',
          sessionId: message.sensitiveSession.id,
          questions: message.sensitiveSession.questions,
          questionIndex: 0,
          answers: {}
        })
      )
      .with(
        {
          state: { status: 'submitting' },
          message: { type: 'verificationReturned', status: 'verified' }
        },
        () => ({ status: 'completed' })
      )
      .with(
        {
          state: { status: 'starting' },
          message: {
            type: 'startReturned',
            status: P.union('identity_not_found', 'insufficient_questions')
          }
        },
        ({ state, message }) => {
          if (state.detailsPromptShown || message.status == 'insufficient_questions') {
            return { status: 'handoff' }
          }
          return { ...state, status: 'identity', detailsPromptShown: true }
        }
      )
      .with(
        {
          state: { status: 'submitting' },
          message: { type: 'verificationReturned', status: 'attempts_exhausted' }
        },
        () => ({ status: 'handoff' })
      )
      .with(
        { state: { status: 'questions' }, message: { type: 'answerSelected' } },
        ({ state, message }) => ({
          ...state,
          answers: { ...state.answers, [message.questionId]: message.sensitiveAnswerId }
        })
      )
      .with(
        { state: { status: 'questions' }, message: { type: 'continueClicked' } },
        ({ state }) => {
          if (state.questionIndex + 1 < state.questions.length) {
            return { ...state, questionIndex: state.questionIndex + 1 }
          }
          return { ...state, status: 'submitting' }
        }
      )
      .with(
        { state: { status: 'questions' }, message: { type: 'previousQuestionClicked' } },
        ({ state }) => {
          if (state.questionIndex == 0) {
            throw new Error('No identity question before the first one')
          }
          return { ...state, questionIndex: state.questionIndex - 1 }
        }
      )
      .with(
        {
          state: { status: 'submitting' },
          message: { type: 'verificationReturned', status: 'failed' }
        },
        ({ state }) => ({ ...session(state), status: 'retryQuiz' })
      )
      .otherwise(({ state, message }) => {
        throw new Error(`Unexpected ${message.type} while identity verification is ${state.status}`)
      })
})

function startKBA(state: IdentityVerificationSession) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* sdk.send({
      type: 'folders/startKBA',
      folderId: state.folderId,
      contractId: state.contractId,
      signerId: state.signerId,
      identity: state.identity
    })
    return {
      type: 'startReturned' as const,
      status: result.status,
      sensitiveSession: result.session
    }
  })
}

function submitKBA(state: QuestionSession) {
  return Effect.gen(function* () {
    const sdk = yield* SdkClient
    const result = yield* sdk.send({
      type: 'folders/submitKBA',
      folderId: state.folderId,
      contractId: state.contractId,
      signerId: state.signerId,
      sessionId: state.sessionId,
      answers: state.answers
    })
    return { type: 'verificationReturned' as const, status: result.status }
  })
}

function session(state: IdentityVerificationSession): IdentityVerificationSession {
  return {
    folderId: state.folderId,
    contractId: state.contractId,
    signerId: state.signerId,
    identity: state.identity,
    detailsPromptShown: state.detailsPromptShown
  }
}

// --- Dialog projection -------------------------------------------------------

export type IdentityVerificationAction = {
  // `close` leaves verification and belongs to the parent; the rest are this module's messages.
  message: IdentityVerificationMessage | { type: 'close' }
  label: string
  variant: 'primary' | 'subtle'
  enabled: boolean
  busy: boolean
}

export type IdentityVerificationDialogModel = {
  title: string
  subtitle: string
  tip?: string
  closeEnabled: boolean
  body:
    | { type: 'identity'; identity: Identity; editable: boolean }
    | {
        type: 'question'
        progressLabel: string
        progress: number
        question: Question
        selectedAnswerId: number | undefined
        answerable: boolean
      }
    | { type: 'notice' }
  actions: IdentityVerificationAction[]
}

/**
 * Everything the verification dialog shows. Undefined once the child has
 * completed, when the parent takes over. An in-flight request disables input
 * and closing; an archived folder disables progress but not leaving.
 */
export function selIdentityVerificationDialog(
  state: IdentityVerificationState,
  isArchived: boolean,
  signaturesSaved: boolean
): IdentityVerificationDialogModel | undefined {
  return match(state)
    .returnType<IdentityVerificationDialogModel | undefined>()
    .with({ status: P.union('identity', 'starting') }, (state) => {
      const busy = state.status == 'starting'
      const complete = Object.values(state.identity).every((value) => value.trim())
      let title = 'Verify your identity'
      let label = 'Continue'
      let tip: string | undefined
      let subtitle =
        'Please answer a few questions to help us verify your identity and protect your tax information.'
      if (state.detailsPromptShown) {
        title = 'Please check your details'
        label = 'Try again'
        subtitle = ''
        tip =
          'We couldn’t match your details. Please double-check your name, date of birth, and Social Security number below and try again.'
      }
      if (busy) label = 'Starting…'
      return {
        title,
        subtitle,
        tip,
        closeEnabled: !busy,
        body: { type: 'identity', identity: state.identity, editable: !busy },
        actions: [
          {
            message: { type: 'startClicked' },
            label,
            variant: 'primary',
            enabled: !busy && !isArchived && complete,
            busy
          }
        ]
      }
    })
    .with({ status: P.union('questions', 'submitting') }, (state) => {
      const busy = state.status == 'submitting'
      const question = state.questions[state.questionIndex]
      if (!question) throw new Error(`Unknown identity question: ${state.questionIndex}`)
      const selectedAnswerId = state.answers[question.id]
      const answered = Object.keys(state.answers).length
      const lastQuestion = state.questionIndex + 1 == state.questions.length
      const back: IdentityVerificationAction[] = []
      if (state.questionIndex > 0) {
        back.push({
          message: { type: 'previousQuestionClicked' },
          label: 'Back',
          variant: 'subtle',
          enabled: !busy,
          busy: false
        })
      }
      let label = 'Continue'
      if (lastQuestion) label = 'Submit answers'
      if (busy) label = 'Checking…'
      const advance: IdentityVerificationAction = {
        message: { type: 'continueClicked' },
        label,
        variant: 'primary',
        enabled: !busy && !isArchived && selectedAnswerId != undefined,
        busy
      }
      return {
        title: 'Verify your identity',
        subtitle: '',
        closeEnabled: !busy,
        body: {
          type: 'question',
          progressLabel: `Question ${state.questionIndex + 1} of ${state.questions.length}`,
          progress: answered / state.questions.length,
          question,
          selectedAnswerId,
          answerable: !busy && !isArchived
        },
        actions: [...back, advance]
      }
    })
    .with({ status: 'retryQuiz' }, () => ({
      title: 'Let’s try another set of questions',
      subtitle:
        'This check didn’t give us a match. You can try another set to complete verification.',
      closeEnabled: true,
      body: { type: 'notice' },
      actions: [
        {
          message: { type: 'startClicked' },
          label: 'Try again',
          variant: 'primary',
          enabled: true,
          busy: false
        }
      ]
    }))
    .with({ status: 'handoff' }, () => ({
      title: 'Your accountant can help from here',
      subtitle: accountantHandoffMessage(signaturesSaved),
      closeEnabled: true,
      body: { type: 'notice' },
      actions: [
        {
          message: { type: 'close' },
          label: 'Close',
          variant: 'primary',
          enabled: true,
          busy: false
        }
      ]
    }))
    .with({ status: 'completed' }, () => undefined)
    .exhaustive()
}

function accountantHandoffMessage(signaturesSaved: boolean): string {
  const sentences = [
    'Online identity checks don’t always work, even when your details are correct. Don’t worry—there are other ways to sign.'
  ]
  sentences.push('Let your accountant know, and they’ll guide you through the next step.')
  if (signaturesSaved) {
    sentences.push('Your signatures are saved, so you won’t lose what you’ve already done.')
  }
  return sentences.join('\n\n')
}
